import {Button, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Skeleton} from "@heroui/react";
import {Spinner} from "@heroui/spinner";
import {addToast} from "@heroui/toast";
import {ProductDetailsResponse, Subscriptions} from "@squareetlabs/capacitor-subscriptions";
import {Product} from "@squareetlabs/capacitor-subscriptions/dist/esm/definitions";
import {useQuery} from "@tanstack/react-query";
import {Check, Crown, Zap} from "lucide-react";
import React, {useEffect, useState} from 'react';
import {apiAction} from "../api.ts";
import PButton from "../components/PButton.tsx";
import {SubscriptionPlanType, SubscriptionTypeToAppleId} from "../constants.ts";
import {useGlobalModal} from "../core/GlobalModal.tsx";
import {useInAppStore} from "../InAppStoreProvider.tsx";
import {useJwtUser} from "../JWTAuthProvider.tsx";
import services from "../services";
import queryKeys from "../utilities/queryKeys.ts";

// What the free tier holds back (PlayerAccess): the dashboard, every event beyond the latest,
// and history past 30 days — then the extras.
const features = [
    'Your dashboard',
    'Every event you play in, open',
    'Your whole history, not just the last 30 days',
    'Pair and head-to-head records',
    'No Ads',
    'Event and profile AI insights',
    'Player Ability Feature',
];

export default function Paywall({
    trialHasExpired = false,
    onClose = () => {
    }
}) {
    const user = useJwtUser()!;
    const globalModal = useGlobalModal();
    const appStore = useInAppStore();

    const pricingPlansQuery = useQuery({
        queryKey: queryKeys.pricing_plans(user.uid),
        queryFn:  () => services.pricing.getPlans(),
    })

    const onCloseMain = () => {
        globalModal.close();
        onClose()
    }

    const planMonthly = SubscriptionPlanType.PlayerMonthly;
    const planYearly = SubscriptionPlanType.PlayerYearly;

    const [inProgress, setInProgress] = useState(false);
    const [billingCycle, setBillingCycle] = useState<'monthly' | 'yearly'>('monthly');
    const isMonthly = billingCycle === 'monthly';

    const handleInAppStoreSubmit = async () => {
        setInProgress(true);

        const subscribed = await appStore.subscribe(currentProductId);
        console.log("[in-app] subscribe", subscribed)

        setInProgress(false);

        if (subscribed) {
            onCloseMain();
        }
    }

    const handleSubmit = async () => {
        setInProgress(true);

        const res = await apiAction('stripe-prepare-payment', {
            plan:          (isMonthly ? planMonthly : planYearly),
            currency_code: pricingPlansQuery.data?.[currentPlan].currency_code,
        });

        if (res.error || !res.redirect_url) {
            setInProgress(false);
            addToast({
                title: res.error || 'Payment cannot be accepted right now. Please try again later.',
                color: 'danger',
            });
            return;
        }

        document.location.href = res.redirect_url;
    };

    const currentPlan = (isMonthly ? planMonthly : planYearly);

    const appleSubscriptionMonthlyId = SubscriptionTypeToAppleId(planMonthly);
    const appleSubscriptionYearlyId = SubscriptionTypeToAppleId(planYearly);

    const currentProductId = (isMonthly ? appleSubscriptionMonthlyId : appleSubscriptionYearlyId);

    const [appStoreProduct, setAppStoreProduct] = useState<Product | undefined>()

    useEffect(() => {
        if (!appStore.enabled || !currentProductId) {
            return;
        }

        setAppStoreProduct(undefined);

        async function loadProduct() {
            let appStoreProduct: ProductDetailsResponse = await Subscriptions.getProductDetails({
                productIdentifier: currentProductId!,
            });

            setAppStoreProduct(appStoreProduct.data);
        }

        loadProduct();

    }, [currentProductId])

    const monthlyPrice = pricingPlansQuery.data?.[planMonthly]?.price;
    const yearlyPrice = pricingPlansQuery.data?.[planYearly]?.price;
    const monthlyRegular = pricingPlansQuery.data?.[planMonthly]?.price_regular;
    const yearlyRegular = pricingPlansQuery.data?.[planYearly]?.price_regular;
    const currencyCode = pricingPlansQuery.data?.[planMonthly]?.currency_code ?? 'USD';
    const discountPercent = pricingPlansQuery.data?.[currentPlan]?.discount_percent;

    return (
        <Modal isOpen={true} placement={"center"} size="lg" onOpenChange={() => {
            onCloseMain();
        }}>
            <ModalContent>
                {() => (
                    <>
                        <ModalHeader className="flex items-center gap-2">
                            <Crown className="w-5 h-5 text-warning" />
                            {trialHasExpired ? 'Your Pro trial has ended' : 'Special Offer'}
                        </ModalHeader>
                        <ModalBody>
                            {trialHasExpired && (
                                <p className="text-default-500 mb-4">
                                    Add a payment method to keep all Pro features &mdash; unlimited tournaments, advanced scheduling, live scoring and player stats.
                                    Or stay on Free with the basics; you can upgrade any time.
                                </p>
                            )}

                            <div className="flex justify-center mb-4">
                                <div className="flex items-center gap-2 bg-default-100 p-1 rounded-lg">
                                    <button
                                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
                                            billingCycle === 'monthly'
                                                ? 'bg-white shadow-sm text-foreground dark:bg-default-200'
                                                : 'text-default-500 hover:text-foreground'
                                        }`}
                                        onClick={() => setBillingCycle('monthly')}
                                    >
                                        Monthly
                                    </button>
                                    <button
                                        className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors flex items-center gap-1.5 ${
                                            billingCycle === 'yearly'
                                                ? 'bg-white shadow-sm text-foreground dark:bg-default-200'
                                                : 'text-default-500 hover:text-foreground'
                                        }`}
                                        onClick={() => setBillingCycle('yearly')}
                                    >
                                        Yearly
                                        <span className="text-xs bg-success-100 text-success-700 px-1.5 py-0.5 rounded">
                                            Save
                                        </span>
                                    </button>
                                </div>
                            </div>

                            {appStore.enabled && <>
                                <div className={"text-center"}>
                                    {appStoreProduct
                                        ? <>
                                            {appStoreProduct?.displayName} {' '}
                                            {appStoreProduct?.price}
                                            {appStoreProduct?.productIdentifier?.includes('monthly') && '/month'}
                                            {appStoreProduct?.productIdentifier?.includes('yearly') && '/year'}

                                            <div>{appStoreProduct?.description}</div>
                                        </>
                                        : <Spinner />
                                    }
                                </div>

                                <div className="text-center">
                                    <PButton isLoading={inProgress} color="primary" isDisabled={user.is_subscribed} onPress={handleInAppStoreSubmit}>
                                        Subscribe
                                    </PButton>
                                </div>
                            </>}

                            {!appStore.enabled && (
                                <Skeleton isLoaded={pricingPlansQuery.isSuccess} className="rounded-lg">
                                    <div className={`p-4 rounded-lg border border-primary bg-primary/5`}>
                                        <div className="flex justify-between items-start mb-3">
                                            <div>
                                                <h4 className="font-semibold flex items-center gap-2">
                                                    Pro
                                                    <span className="text-xs bg-primary text-white px-2 py-0.5 rounded">
                                                        Recommended
                                                    </span>
                                                </h4>
                                                <div className="mt-1">
                                                    {isMonthly ? (
                                                        <p className="text-sm text-default-500">
                                                            {!!monthlyRegular && <span className="line-through mr-1">{monthlyRegular}</span>}
                                                            <strong className="text-foreground text-lg">{monthlyPrice} {currencyCode}/month</strong>
                                                            {!!discountPercent && <span className="ml-2 text-xs bg-success-100 text-success-700 px-1.5 py-0.5 rounded">{discountPercent}% off!</span>}
                                                        </p>
                                                    ) : (
                                                        <p className="text-sm text-default-500">
                                                            {!!yearlyRegular && <span className="line-through mr-1">{yearlyRegular}</span>}
                                                            <strong className="text-foreground text-lg">{yearlyPrice} {currencyCode}/year</strong>
                                                            {!!discountPercent && <span className="ml-2 text-xs bg-success-100 text-success-700 px-1.5 py-0.5 rounded">{discountPercent}% off!</span>}
                                                        </p>
                                                    )}
                                                </div>
                                            </div>
                                            <Button
                                                size="sm"
                                                color="primary"
                                                startContent={<Zap className="w-4 h-4" />}
                                                onPress={handleSubmit}
                                                isLoading={inProgress}
                                            >
                                                Upgrade
                                            </Button>
                                        </div>
                                        <ul className="text-sm space-y-1">
                                            {features.map((feature, idx) => (
                                                <li key={idx} className="flex items-center gap-2 text-default-600">
                                                    <Check className="w-3 h-3 text-success" />
                                                    {feature}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </Skeleton>
                            )}

                            {!appStore.enabled && (
                                <p className="text-xs text-default-400 mt-2 text-center">
                                    Cancel anytime. Secure payment via Stripe.
                                </p>
                            )}

                            {appStore.enabled && <>
                                <PButton variant={"light"} onPress={() => appStore.restorePurchases()}>Restore purchases</PButton>
                            </>}
                        </ModalBody>
                        <ModalFooter>
                            <Button variant="flat" onPress={onCloseMain}>
                                Maybe Later
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
};
